import * as React from "react";
import { createPortal } from "react-dom";
import { cva, type VariantProps } from "class-variance-authority";
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  X,
} from "lucide-react";

import { cn } from "../lib/utils";

/**
 * Transient confirmations — "Saved", "Category deleted", "Couldn't post grades".
 *
 * The thing this replaces was an `<Alert>` rendered in the page's own flow, so
 * every save pushed the content below it down by the height of the banner and
 * pulled it back up when the banner cleared. A toast is deliberately NOT in the
 * flow: the stack is a `fixed` overlay portalled to `document.body`, so showing
 * and hiding one cannot reflow anything.
 *
 * An `<Alert>` is still the right call for a message that belongs to a
 * particular place on the page and should stay there — a form's validation
 * summary, a "this course has no categories yet" explainer. Toasts are for
 * feedback about an action that just completed.
 */

export type ToastVariant = "success" | "error" | "warning" | "info";

export interface ToastOptions {
  /**
   * Milliseconds on screen. `null` pins the toast until it is dismissed —
   * which is the default for errors, since a message you may need to act on
   * shouldn't time out while you're reading it.
   */
  duration?: number | null;
  /** Optional bold line above the message. */
  title?: string;
}

export interface ToastRecord extends Required<Pick<ToastOptions, "duration">> {
  id: string;
  variant: ToastVariant;
  message: React.ReactNode;
  title?: string;
}

export interface ToastApi {
  success: (message: React.ReactNode, options?: ToastOptions) => string;
  error: (message: React.ReactNode, options?: ToastOptions) => string;
  warning: (message: React.ReactNode, options?: ToastOptions) => string;
  info: (message: React.ReactNode, options?: ToastOptions) => string;
  /** Fire a toast of any variant. The four helpers above wrap this. */
  show: (
    variant: ToastVariant,
    message: React.ReactNode,
    options?: ToastOptions,
  ) => string;
  /** Dismiss one toast by the id its firing call returned. */
  dismiss: (id: string) => void;
  /** Dismiss every open toast — e.g. when a route change makes them stale. */
  dismissAll: () => void;
}

/** Long enough to read a short sentence; errors default to no timeout at all. */
const DEFAULT_DURATION = 4000;
/** Beyond that, a longer message earns proportionally longer to read it. */
const READING_MS_PER_CHAR = 55;
const FREE_CHARS = 45;
const MAX_DURATION = 10_000;

/**
 * The banners this replaced set their own timeout per message — 3s for "Saved",
 * 6s for a two-clause sentence about where withdrawn students went. That
 * instinct was right and worth keeping, but it isn't something each call site
 * should have to decide, so it's derived from the message instead.
 */
function readingTime(message: React.ReactNode): number {
  if (typeof message !== "string") return DEFAULT_DURATION;
  const over = Math.max(0, message.length - FREE_CHARS);
  return Math.min(MAX_DURATION, DEFAULT_DURATION + over * READING_MS_PER_CHAR);
}
/** Kept in step with the `duration-200` on the exit animation below. */
const EXIT_MS = 200;
const DEFAULT_LIMIT = 4;

const ToastContext = React.createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = React.useContext(ToastContext);
  if (!api) {
    throw new Error(
      "useToast must be used inside a <ToastProvider>. Mount one once, at the app root.",
    );
  }
  return api;
}

export interface ToastProviderProps {
  children: React.ReactNode;
  /** Oldest toasts fall off the top once this many are on screen. */
  limit?: number;
  /**
   * Where the stack anchors. The default sits out of the way of the header and
   * of a page's primary actions.
   */
  position?: "bottom-right" | "top-right";
}

export function ToastProvider({
  children,
  limit = DEFAULT_LIMIT,
  position = "bottom-right",
}: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<ToastRecord[]>([]);
  const [closing, setClosing] = React.useState<string[]>([]);
  const [paused, setPaused] = React.useState(false);
  const counter = React.useRef(0);

  // Every pending removal, so unmounting the provider mid-animation doesn't
  // leave a timer to fire against a dead component.
  const exitTimers = React.useRef(new Map<string, ReturnType<typeof setTimeout>>());

  React.useEffect(() => {
    const timers = exitTimers.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  const dismiss = React.useCallback((id: string) => {
    if (exitTimers.current.has(id)) return; // already on its way out
    setClosing((open) => [...open, id]);
    const timer = setTimeout(() => {
      exitTimers.current.delete(id);
      setToasts((open) => open.filter((t) => t.id !== id));
      setClosing((open) => open.filter((openId) => openId !== id));
    }, EXIT_MS);
    exitTimers.current.set(id, timer);
  }, []);

  const dismissAll = React.useCallback(() => {
    setToasts((open) => {
      open.forEach((t) => dismiss(t.id));
      return open;
    });
  }, [dismiss]);

  const show = React.useCallback<ToastApi["show"]>(
    (variant, message, options = {}) => {
      counter.current += 1;
      const id = `toast-${counter.current}`;
      const duration =
        options.duration === undefined
          ? // An error is the one kind you may need to act on, so it waits.
            variant === "error"
            ? null
            : readingTime(message)
          : options.duration;

      setToasts((open) =>
        // Dropping from the front keeps the newest — a burst of saves should
        // leave you looking at the most recent one, not the first.
        [...open, { id, variant, message, title: options.title, duration }].slice(
          -limit,
        ),
      );
      return id;
    },
    [limit],
  );

  const api = React.useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      dismissAll,
      success: (message, options) => show("success", message, options),
      error: (message, options) => show("error", message, options),
      warning: (message, options) => show("warning", message, options),
      info: (message, options) => show("info", message, options),
    }),
    [show, dismiss, dismissAll],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport
        toasts={toasts}
        closing={closing}
        paused={paused}
        position={position}
        onPause={() => setPaused(true)}
        onResume={() => setPaused(false)}
        onDismiss={dismiss}
      />
    </ToastContext.Provider>
  );
}

const viewportPosition = {
  // Full width on a phone, where a 380px card pinned to one corner would just
  // look stranded; anchored to the corner from `sm` up.
  "bottom-right":
    "bottom-0 left-0 right-0 flex-col sm:bottom-6 sm:left-auto sm:right-6",
  "top-right": "top-0 left-0 right-0 flex-col sm:top-6 sm:left-auto sm:right-6",
} as const;

interface ToastViewportProps {
  toasts: ToastRecord[];
  closing: string[];
  paused: boolean;
  position: NonNullable<ToastProviderProps["position"]>;
  onPause: () => void;
  onResume: () => void;
  onDismiss: (id: string) => void;
}

function ToastViewport({
  toasts,
  closing,
  paused,
  position,
  onPause,
  onResume,
  onDismiss,
}: ToastViewportProps) {
  // `document` is absent during SSR and in a node-environment test; rendering
  // nothing there is correct, since there is no viewport to pin a toast to.
  if (typeof document === "undefined" || toasts.length === 0) return null;

  return createPortal(
    <div
      role="region"
      aria-label="Notifications"
      // z-50 matches the dialog/sheet overlays, so a confirmation raised from
      // inside a sheet still lands on top of it.
      className={cn(
        "pointer-events-none fixed z-50 flex max-h-screen w-full gap-2 p-4 sm:max-w-100 sm:p-0",
        viewportPosition[position],
      )}
      onMouseEnter={onPause}
      onMouseLeave={onResume}
      onFocusCapture={onPause}
      onBlurCapture={onResume}
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          closing={closing.includes(toast.id)}
          paused={paused}
          onDismiss={onDismiss}
        />
      ))}
    </div>,
    document.body,
  );
}

const toastVariants = cva(
  cn(
    // `pointer-events-auto` re-enables the card itself; the viewport around it
    // stays click-through so the gaps never eat a click meant for the page.
    "pointer-events-auto relative flex w-full items-start gap-3 rounded-lg border p-4 shadow-lg",
    "transition-all data-[state=open]:animate-in data-[state=closed]:animate-out duration-200",
    "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
    "data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:slide-out-to-right-4",
    // Someone who asked for less motion still gets the fade, just not the slide.
    "motion-reduce:transition-none motion-reduce:data-[state=open]:slide-in-from-bottom-0",
  ),
  {
    variants: {
      variant: {
        success: "border-success-200 bg-success-50 text-success-900",
        error: "border-danger-200 bg-danger-50 text-danger-900",
        warning: "border-warning-200 bg-warning-50 text-warning-900",
        info: "border-info-200 bg-info-50 text-info-900",
      },
    },
    defaultVariants: { variant: "info" },
  },
);

const iconFor: Record<ToastVariant, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const iconColor: Record<ToastVariant, string> = {
  success: "text-success-600",
  error: "text-danger-600",
  warning: "text-warning-600",
  info: "text-info-600",
};

interface ToastItemProps {
  toast: ToastRecord;
  closing: boolean;
  paused: boolean;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, closing, paused, onDismiss }: ToastItemProps) {
  const { id, variant, message, title, duration } = toast;
  const Icon = iconFor[variant];

  // What's left of the countdown. Hovering the stack banks the remainder rather
  // than restarting it, so a toast you glanced at doesn't get a fresh 4s every
  // time the pointer crosses it.
  const remaining = React.useRef(duration ?? 0);

  React.useEffect(() => {
    if (duration == null || paused || closing) return;

    const startedAt = Date.now();
    const timer = setTimeout(() => onDismiss(id), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt));
    };
  }, [id, duration, paused, closing, onDismiss]);

  return (
    <div
      // `alert` interrupts, `status` waits its turn. An error is the only one
      // worth cutting into whatever a screen reader is already saying.
      role={variant === "error" ? "alert" : "status"}
      aria-atomic="true"
      data-state={closing ? "closed" : "open"}
      className={toastVariants({ variant })}
    >
      <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", iconColor[variant])} aria-hidden="true" />
      <div className="flex-1 text-sm">
        {title && <p className="font-medium">{title}</p>}
        <div className={cn(title && "mt-0.5")}>{message}</div>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(id)}
        aria-label="Dismiss notification"
        className="-m-1 shrink-0 rounded-sm p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export type ToastCardVariants = VariantProps<typeof toastVariants>;
